<script>
import {
  getDirectoryItem,
  getDirectoryItemsByCodes,
} from "@/entities/directory";

import Button from "@/shared/ui/button/Button.vue";
import Checkbox from "@/shared/ui/checkbox/Checkbox.vue";
import { FormSheet } from "@/shared/ui/form-sheet";
import { Input } from "@/shared/ui/input";
import { Spinner } from "@/shared/ui/spinner";

export default {
  name: "CreateAction",
  components: { Button, Checkbox, FormSheet, Input, Spinner },
  props: {
    editingKey: { type: String, default: null },
    template: { type: Object, default: () => ({}) },
    form: { type: Object, default: () => ({ codes: [] }) },
  },

  data() {
    return {
      visible: false,
      loading: false,
      directories: [],
      actionLabels: {},
      searchQuery: "",
      total: 0,
    };
  },

  computed: {
    filteredDirectories() {
      if (!this.searchQuery) return this.directories;

      const q = this.searchQuery.toLowerCase();

      return this.directories.filter((item) =>
        this.getActionLabel(item.code).toLowerCase().includes(q)
      );
    },

    isAllSelected() {
      if (!this.filteredDirectories.length) return false;

      return this.filteredDirectories.every((item) =>
        this.form.codes.includes(item.code)
      );
    },

    isIndeterminate() {
      const selectedCount = this.filteredDirectories.filter((item) =>
        this.form.codes.includes(item.code)
      ).length;

      return (
        selectedCount > 0 && selectedCount < this.filteredDirectories.length
      );
    },
  },

  async created() {
    await this.loadDirectories();
  },

  mounted() {
    this.visible = true;
  },

  methods: {
    async loadDirectories() {
      this.loading = true;
      try {
        const data = await getDirectoryItem("notarial_action_category");

        if (data?.success) {
          this.directories = data.payload?.children || [];
          this.total = data.payload?.total || 0;
          await this.getActionLabelsForItems(this.directories);
        }
      } finally {
        this.loading = false;
      }
    },

    async getActionLabelsForItems(items) {
      const codes = [...new Set(items.map((item) => item.code))].filter(
        (code) => !this.actionLabels[code]
      );

      if (!codes.length) return;

      const data = await getDirectoryItemsByCodes({ codes });

      if (data?.success && data.payload?.items?.length) {
        data.payload.items.forEach((item) => {
          this.actionLabels[item.code] = item.name || item.code;
        });
      }
    },

    getActionLabel(code) {
      return this.actionLabels[code] || code;
    },

    handleClose() {
      this.visible = false;
    },
  },
};
</script>

<template>
  <FormSheet
    v-model:open="visible"
    :title="$t('Вид действия')"
    :width="600"
    @close="handleClose"
  >
    <div class="w-full h-full flex flex-col gap-4">
      <Input v-model="searchQuery" :placeholder="$t('Название категории')" />

      <main class="flex-1 overflow-y-auto flex flex-col gap-4">
        <div v-if="loading" class="flex justify-center items-center py-8">
          <Spinner size="sm" />
        </div>
        <div v-if="!loading" class="group">
          <ul>
            <li v-for="action in filteredDirectories" :key="action.code">
              <Checkbox v-model="form.codes" :label="action.code">
                <div
                  class="text-sm font-medium break-words whitespace-normal leading-6 pl-1.5 line-clamp-2"
                  :title="getActionLabel(action.code)"
                >
                  {{ getActionLabel(action.code) }}
                </div>
              </Checkbox>
            </li>
          </ul>
        </div>
      </main>
    </div>

    <template #footer>
      <div class="flex-1"></div>
      <Button variant="outline" @click="handleClose">
        {{ $t("Отмена") }}
      </Button>
      <Button class="ml-2" variant="default" @click="handleClose">
        {{ $t("Сохранить") }}
      </Button>
    </template>
  </FormSheet>
</template>

<style scoped lang="scss">
.group {
  @apply w-full flex flex-col gap-4;

  ul {
    @apply w-full border border-solid border-border flex flex-col justify-start items-start overflow-hidden;
    border-radius: var(--radius);

    li {
      @apply w-full px-4 py-3 bg-card border-b border-border flex flex-col justify-center items-start gap-4;

      &:last-child {
        @apply border-b-0;
      }

      &:hover {
        @apply bg-muted;
      }
    }
  }
}
</style>
